import styles from './ReportCardHeader.module.css';

export interface ReportCardHeaderProps {
  programName: string;
  baciCode: string;
  organization: string | null;
  foundingYear: number | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  showPrintButton?: boolean;
}

/**
 * ReportCardHeader - Professional letterhead-style header for printable reports
 *
 * Features:
 * - Large Book Award Mastery logo (centered)
 * - Program name and BACI code
 * - Contact information
 * - Date of report generation
 * - Light yellow print button
 */
export function ReportCardHeader({
  programName,
  baciCode,
  organization,
  foundingYear,
  email,
  phone,
  website,
  showPrintButton = true
}: ReportCardHeaderProps) {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <header className={styles.letterhead}>
      {/* Logo */}
      <img src="/BAM-logo-embedded.svg" alt="Book Award Mastery" className={styles.largeLogo} />

      {/* Program Info */}
      <h1 className={styles.programName}>{programName}</h1>
      <p className={styles.baciCode}>BACI Code: {baciCode}</p>

      {/* Organization & Year */}
      {organization && (
        <p className={styles.organization}>
          {organization}
          {foundingYear && ` (Est. ${foundingYear})`}
        </p>
      )}

      {/* Contact Info */}
      {(email || phone || website) && (
        <div className={styles.contactInfo}>
          {email && (
            <p className={styles.contactItem}>
              <span className={styles.contactIcon}>✉</span>
              <a href={`mailto:${email}`}>{email}</a>
            </p>
          )}
          {phone && (
            <p className={styles.contactItem}>
              <span className={styles.contactIcon}>📞</span>
              {phone}
            </p>
          )}
          {website && (
            <p className={styles.contactItem}>
              <span className={styles.contactIcon}>🌐</span>
              <a href={website} target="_blank" rel="noopener noreferrer">
                {website}
              </a>
            </p>
          )}
        </div>
      )}

      {/* Report Date */}
      <p className={styles.reportDate}>Report Generated: {currentDate}</p>

      {/* Print Button */}
      {showPrintButton ? (
        <button type="button" onClick={handlePrint} className={styles.printButton}>
          Print Report Card
        </button>
      ) : null}
    </header>
  );
}

export default ReportCardHeader;
